import type { DashboardConfig, Widget } from './config';
import { statusSources, widgetMetrics } from './config';
import type { FilterClause, FilterCondition } from './filters';

export interface FetchSpec {
  filter?: FilterClause[];
  requiredWindowMs?: number;
}

export interface ResourceBackfill {
  specs: FetchSpec[];
}

export type ConnectorBackfill = Map<string, ResourceBackfill>;

const WINDOW_UNIT_MS: Record<string, number> = {
  h: 3_600_000,
  d: 86_400_000,
  w: 604_800_000,
  m: 2_592_000_000,
};

function parseWindowMs(window: string): number | undefined {
  const match = /^(\d+)(h|d|w|m)$/.exec(window);
  if (!match) {
    return undefined;
  }
  const unitMs = WINDOW_UNIT_MS[match[2]!];
  if (unitMs === undefined) {
    return undefined;
  }
  return parseInt(match[1]!) * unitMs;
}

interface WidgetMetricRef {
  connectorName: string;
  resourceName: string | undefined;
  windowMs: number | undefined;
  filter: FilterClause[] | undefined;
}

function widgetMetricRefs(widget: Widget): WidgetMetricRef[] {
  if (widget.kind === 'status') {
    return statusSources(widget).map((source) => ({
      connectorName: source,
      resourceName: undefined,
      windowMs: undefined,
      filter: undefined,
    }));
  }
  return widgetMetrics(widget).map((metric) => {
    const windowStr =
      widget.kind === 'stat' ? (widget.window ?? metric.window) : widget.window;
    return {
      connectorName: metric.connectorId,
      resourceName: metric.name ?? metric.entityType,
      windowMs: windowStr ? parseWindowMs(windowStr) : undefined,
      filter: metric.filter,
    };
  });
}

function conditionKey(c: FilterCondition): string {
  return `${c.field} ${c.op} ${String(c.value)}`;
}

function clauseKey(clause: FilterClause): string {
  if ('or' in clause) {
    return `or(${clause.or.map(conditionKey).sort().join('|')})`;
  }
  return conditionKey(clause);
}

function filterKey(filter: FilterClause[] | undefined): string {
  if (!filter || filter.length === 0) {
    return '';
  }
  return filter.map(clauseKey).sort().join('&');
}

function mergeSpecs(specs: FetchSpec[]): FetchSpec[] {
  interface Group {
    filter: FilterClause[] | undefined;
    windowMs: number | undefined;
    unbounded: boolean;
  }
  const groups = new Map<string, Group>();
  for (const spec of specs) {
    const key = filterKey(spec.filter);
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        filter: spec.filter,
        windowMs: spec.requiredWindowMs,
        unbounded: spec.requiredWindowMs === undefined,
      });
      continue;
    }
    if (spec.requiredWindowMs === undefined) {
      existing.unbounded = true;
    } else if (
      existing.windowMs === undefined ||
      spec.requiredWindowMs > existing.windowMs
    ) {
      existing.windowMs = spec.requiredWindowMs;
    }
  }
  const out: FetchSpec[] = [];
  for (const group of groups.values()) {
    const spec: FetchSpec = {};
    if (group.filter !== undefined) {
      spec.filter = group.filter;
    }
    if (!group.unbounded && group.windowMs !== undefined) {
      spec.requiredWindowMs = group.windowMs;
    }
    out.push(spec);
  }
  return out;
}

export function computeConnectorBackfill(
  config: DashboardConfig,
): Map<string, ConnectorBackfill> {
  const raw = new Map<string, Map<string, FetchSpec[]>>();
  for (const dashboard of Object.values(config.dashboards)) {
    for (const widget of Object.values(dashboard.widgets)) {
      for (const ref of widgetMetricRefs(widget)) {
        const { connectorName, resourceName, windowMs, filter } = ref;
        let resources = raw.get(connectorName);
        if (!resources) {
          resources = new Map<string, FetchSpec[]>();
          raw.set(connectorName, resources);
        }
        if (resourceName === undefined) {
          continue;
        }
        const spec: FetchSpec = {};
        if (filter !== undefined) {
          spec.filter = filter;
        }
        if (windowMs !== undefined) {
          spec.requiredWindowMs = windowMs;
        }
        const list = resources.get(resourceName);
        if (list) {
          list.push(spec);
        } else {
          resources.set(resourceName, [spec]);
        }
      }
    }
  }
  const result = new Map<string, ConnectorBackfill>();
  for (const [connectorName, resources] of raw) {
    const merged: ConnectorBackfill = new Map<string, ResourceBackfill>();
    for (const [resourceName, specs] of resources) {
      merged.set(resourceName, { specs: mergeSpecs(specs) });
    }
    result.set(connectorName, merged);
  }
  return result;
}

export function fetchSpecsForConnector(
  config: DashboardConfig,
  connectorName: string,
): Record<string, FetchSpec[]> | undefined {
  const resources = computeConnectorBackfill(config).get(connectorName);
  if (!resources) {
    return undefined;
  }
  const out: Record<string, FetchSpec[]> = {};
  for (const [resourceName, { specs }] of resources) {
    out[resourceName] = specs;
  }
  return out;
}
